import { Box, Skeleton } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import EvidenceViewerDialog from "features/programmesActions/EvidenceViewerDialog";
import { FILE_UPLOAD_TYPE, FileUpload, useEventFiles } from "model";
import { useState } from "react";
import { getSingleFileUploadViaFileEntryApi } from "services";

function getMimeType(fileName: string): string {
  const ext = fileName.split(".").pop()?.toLowerCase();
  switch (ext) {
    case "png":
      return "image/png";
    case "gif":
      return "image/gif";
    case "webp":
      return "image/webp";
    default:
      return "image/jpeg";
  }
}

function PhotoThumbnail({
  fileEntryGuid,
  onClick,
}: {
  fileEntryGuid: string;
  onClick: () => void;
}) {
  const { data, isLoading } = useQuery<FileUpload>({
    queryKey: ["fileEntryThumbnail", fileEntryGuid],
    queryFn: () => getSingleFileUploadViaFileEntryApi({ guid: fileEntryGuid }),
  });

  if (isLoading) {
    return <Skeleton variant="rounded" width={56} height={56} />;
  }
  if (!data) {
    return null;
  }

  return (
    <Box
      component="img"
      src={`data:${getMimeType(data.fileName)};base64,${data.file}`}
      onClick={onClick}
      sx={{
        width: 56,
        height: 56,
        objectFit: "cover",
        borderRadius: "6px",
        cursor: "pointer",
        border: "1px solid rgba(0,0,0,0.12)",
      }}
    />
  );
}

// Shows photo thumbnails attached to a FarmEvent (via EventFileEntry), with a
// click-to-enlarge viewer. Renders nothing if the event has no photos — safe
// to use unconditionally, including for event types with no FarmEvent at all
// (grazing events), since useEventFiles then just finds no matches.
export default function EventPhotos({
  farmEventGuid,
}: {
  farmEventGuid: string;
}) {
  const { data: files = [] } = useEventFiles(farmEventGuid);
  const photos = files.filter(
    (f) => f.fileUploadType === FILE_UPLOAD_TYPE.ACTION_EVIDENCE_PHOTO.valueOf()
  );
  const [viewerFile, setViewerFile] = useState<{
    guid: string;
    name: string;
  } | null>(null);

  if (photos.length === 0) {
    return null;
  }

  return (
    <>
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}>
        {photos.map((photo) => (
          <PhotoThumbnail
            key={photo.guid}
            fileEntryGuid={photo.fileEntryGuid}
            onClick={() =>
              setViewerFile({ guid: photo.fileEntryGuid, name: photo.fileName })
            }
          />
        ))}
      </Box>
      {viewerFile && (
        <EvidenceViewerDialog
          open={!!viewerFile}
          fileEntryGuid={viewerFile.guid}
          fileName={viewerFile.name}
          onClose={() => setViewerFile(null)}
        />
      )}
    </>
  );
}
